import {getTestProps} from '@benchling/js-test-utils';
import {createStyles, TextInput} from '@mantine/core';
import React, {ChangeEvent, FocusEvent, ReactNode, Ref, useMemo} from 'react';

import HelpButton from 'help/HelpButton';
import {FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {FloatingInteraction} from 'ui-library/floating-ui/FloatingConstants';
import {InputErrorMessageStyle, InputProps, LabelStyle, Size} from 'ui-library/inputs/InputConstants';
import {ElementKeyboardEvent} from 'util/Types';

const useStyles = createStyles(
  (
    theme,
    {error, hasError, labelStyle, size}: Pick<TextFieldProps, 'error' | 'hasError' | 'labelStyle' | 'size'>
  ) => ({
    description: {
      fontSize: theme.fontSizes.sm,
    },
    error: {
      // If we have passed hasError and not error.message, hide the error div so as to not increase the height
      // of the input
      display: hasError && !error ? 'none' : 'block',
      fontSize: theme.fontSizes.xs,
      color: error?.style === InputErrorMessageStyle.INFO ? theme.colors.gray[7] : theme.colors.red[9],
    },
    input: {
      '&:invalid': {
        // Override Benchling styles when the input is required and empty
        backgroundColor: theme.white,
        borderColor: theme.colors.gray[3],
        color: theme.colors.gray[9],
      },
      '&[data-invalid]': {
        // Override Mantine styles when the input has an error
        borderColor:
          error?.style === InputErrorMessageStyle.INFO ? theme.colors.gray[3] : theme.colors.red[8],
        color: theme.colors.gray[9],
      },
    },
    label: {
      color: labelStyle === LabelStyle.LABEL ? theme.colors.gray[9] : theme.colors.gray[7],
      fontSize: labelStyle === LabelStyle.LABEL ? theme.fontSizes.md : theme.fontSizes.sm,
      fontWeight: labelStyle === LabelStyle.LABEL ? 'bold' : FONT_WEIGHT_PROXIMA_SEMIBOLD,
    },
    // The following two classes only apply / are applied when type=TextInputType.PASSWORD. Otherwise they
    // have no effect
    innerInput: {
      height: size === Size.SMALL ? '28px' : '36px',
    },
    visibilityToggle: {
      borderRadius: '14px',
      height: '28px',
      minWidth: '28px',
      width: '28px',
    },
  })
);

export interface TextFieldProps extends InputProps {
  autoFocus?: boolean;
  autoComplete?: string;
  defaultValue?: string;
  /** @deprecated - use `isDisabled` on parent {@link InputProps} */
  disabled?: boolean;
  hasError?: boolean;
  label?: string;
  name?: string;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: ElementKeyboardEvent) => void;
  onPaste?: (e: React.ClipboardEvent<HTMLInputElement>) => void;
  readOnly?: boolean;
  placeholder?: string;
  value?: string;
  maxLength?: number;
  dataTestAttrs?: DataTestAttrs;
  textInputRef?: Ref<HTMLInputElement>;
  helpInteraction?: FloatingInteraction.CLICK | FloatingInteraction.HOVER;
}

/**
 * Component for input of string fields, providing labels, icons, and validation.
 * @deprecated Use Mantine's TextInput instead
 */
export default function TextField({
  autoFocus = false,
  disabled = false,
  isDisabled = false,
  isRequired = false,
  labelStyle = LabelStyle.LABEL,
  size = Size.SMALL,
  ...otherProps
}: TextFieldProps): JSX.Element {
  const {classes, cx} = useStyles({error: otherProps.error, hasError: otherProps.hasError, labelStyle, size});
  const classNames = useMemo(
    () => ({
      ...classes,
      label: cx(classes.label, labelStyle === LabelStyle.HIDDEN ? 'sr-only' : ''),
      input: cx(classes.input, otherProps.inputClassName),
      visibilityToggle: cx(classes.visibilityToggle, 'button--circleIcon'),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- classes should only change if error / labelStyle change
    [otherProps.error, otherProps.hasError, labelStyle, otherProps.inputClassName, size]
  );

  let error: ReactNode = null;
  if (otherProps.error) {
    error = otherProps.error.message;
  } else if (otherProps.hasError) {
    error = <></>;
  }

  return (
    <TextInput
      autoComplete={otherProps.autoComplete}
      autoFocus={autoFocus}
      className={otherProps.className}
      defaultValue={otherProps.defaultValue}
      disabled={disabled || isDisabled}
      classNames={classNames}
      description={otherProps.description}
      error={error}
      ref={otherProps.textInputRef}
      label={
        <>
          {otherProps.label}
          {otherProps.help && (
            <HelpButton
              renderContent={() => otherProps.help}
              interaction={otherProps.helpInteraction}
              iconClassName="u-mini-spacing-left"
            />
          )}
        </>
      }
      maxLength={otherProps.maxLength}
      name={otherProps.name}
      onBlur={otherProps.onBlur}
      onChange={otherProps.onChange}
      onKeyDown={otherProps.onKeyDown}
      onPaste={otherProps.onPaste}
      placeholder={otherProps.placeholder}
      readOnly={otherProps.readOnly}
      required={isRequired}
      rightSection={otherProps.button}
      value={otherProps.value}
      {...getTestProps(otherProps.dataTestAttrs)}
    />
  );
}
